"use client";

import { useEffect, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";

/**
 * 画像 1 枚を全画面モーダルで拡大表示するビューア。
 * MainVisual の SP タップ拡大で使う。
 *
 * Portal で body 直下に出す: 親に transform / perspective が当たると position:fixed が
 * viewport ではなく親基準になり inset:0 が画面を覆わなくなる (Chrome / iOS Safari)。
 */
export function ImageViewer({
  imageUrl,
  altText,
  caption,
  onClose,
}: {
  imageUrl: string;
  altText: string;
  caption: string | null;
  onClose: () => void;
}) {
  // SSR で document を触らないように、 useEffect+setState の再 render cascade ではなく
  // useSyncExternalStore で server=false / client=true を即時返す。
  const mounted = useSyncExternalStore(
    () => () => {},
    () => true,
    () => false,
  );

  // 表示中は body スクロールロック + Esc で閉じる。
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose]);

  if (!mounted) return null;
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={altText}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        backgroundColor: "#000000",
        display: "flex",
        flexDirection: "column",
        paddingTop: "env(safe-area-inset-top, 0px)",
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
      }}
    >
      {/* 画像領域の上下余白が均等になるよう absolute で重ねる (flex flow から外す)。 */}
      <button
        // 閉じるだけで、押されなかったことに情報が無い
        data-analytics-ignore
        type="button"
        onClick={onClose}
        aria-label="閉じる"
        style={{
          position: "absolute",
          top: "calc(env(safe-area-inset-top, 0px) + 12px)",
          right: "12px",
          width: "44px",
          height: "44px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#ffffff",
          background: "transparent",
          border: "none",
          padding: 0,
          cursor: "pointer",
          zIndex: 1,
        }}
      >
        <svg
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <line x1="6" y1="6" x2="18" y2="18" />
          <line x1="18" y1="6" x2="6" y2="18" />
        </svg>
      </button>
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "0 8px",
          minHeight: 0,
        }}
      >
        {/* next/image は pinch-zoom と相性が悪いので素の img を使う。 */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={imageUrl}
          alt={altText}
          draggable={false}
          style={{
            maxWidth: "100%",
            maxHeight: "100%",
            objectFit: "contain",
            touchAction: "pinch-zoom",
          }}
        />
      </div>
      {caption !== null && caption !== "" && (
        <p
          className="font-notojp"
          style={{
            width: "100%",
            padding: "16px 16px",
            color: "#ffffff",
            fontSize: "14px",
            lineHeight: 1.6,
            flexShrink: 0,
          }}
        >
          {caption}
        </p>
      )}
    </div>,
    document.body,
  );
}
